import * as d3 from 'd3'
import { CLOUD_PLACE_SIZE } from './constants.ts'

/**
 * The struts and springs.
 *
 * ⚠⚠ ONE TABLE SETS THE SHAPE, AND THAT IS THE WHOLE POINT OF THIS FILE. `LAYER_RADIUS` says
 * which ring a type sits on, measured from the account in the middle. Three things read it and
 * they may never disagree: `layout.ts` seeds a new node on that ring, `layerForce` holds it near
 * it, and a link's rest length is the GAP between its two ends' rings.
 *
 * ⚠ They used to be three separate tables and they DID disagree. `layout.ts` built its own ladder
 * by summing a table keyed by a link's SOURCE type, which put the node ring at 240 and the leaf
 * ring at 247 — a 7 px gap between glyphs whose drawn radii already sum to 15; and `forceRadial`'s
 * radius came from that table, which is 0 for `service`, so the one force that could have kept the
 * layers apart was instead pulling every leaf to the centre of the picture.
 */
export const LAYER_RADIUS: Record<GraphNode['type'], number> = {
  account: 0,
  network: 70,
  location: 145,
  node: 225,
  // Close in, so a node's own services make a thin band the fans over it have to cross rather
  // than a wide one.
  service: 268,
  llm: 268,
}

/** Distance to fall back on for a link between two types with no ring of their own. */
const DEFAULT_LINK_DISTANCE = 60

export const EXPANDED_FORCE = {
  linkExtension: 60,
  collisionMultiplier: 4,
}

const CHARGE_BASE: Record<GraphNode['type'], number> = {
  account: -260,
  network: -220,
  location: -120,
  node: -110,
  service: -30,
  llm: -30,
}

/**
 * Keep-out radius per type, in the units the glyphs are actually drawn at (`renderer.ts`):
 * account 26, network ~11, node 10, service 3-6 collapsed. Each is the drawn radius
 * plus the room its label wants — labels sit `TEXT_OFFSET` below the glyph and nothing else in
 * the model knows they exist.
 */
const COLLIDE_BASE: Record<GraphNode['type'], number> = {
  account: 40,
  network: 30,
  location: 0,
  node: 30,
  service: 11,
  llm: 11,
}

const SIBLING_MAX = 40
const COLLIDE_SIBLING_MAX_FACTOR = 1.6 // factor at max sibling count
const COLLIDE_SIBLING_MIN_FACTOR = 1 // factor at 0-1 sibling
/**
 * ⚠ THIS RAMP USED TO RUN THE OTHER WAY — 2 at one sibling, falling to 1 at forty — so the more
 * crowded a ring became the LESS its members pushed each other apart. Crowding is the one
 * condition under which repulsion has anything to do; a thirteen-node site got the weakest
 * charge in the graph.
 */
const CHARGE_SIBLING_MAX_FACTOR = 2.2 // factor at max sibling count
const CHARGE_SIBLING_MIN_FACTOR = 1 // factor at 0-1 sibling

export default class Force {
  private root?: GraphNode
  private degrees: Map<string, number> = new Map()
  private siblings: Map<string, number> = new Map()
  private angles: Map<string, number> = new Map()

  setDegrees(degrees: Map<string, number>) {
    this.degrees = degrees
  }

  setSiblings(siblings: Map<string, number>) {
    this.siblings = siblings
  }

  /** The bearing each node's parent gave it — `Layout.plan`, recomputed on every update. */
  setAngles(angles: Map<string, number>) {
    this.angles = angles
    // ⚠ The cached root goes with them. A node the adapter has dropped is a centre that no longer
    // moves with the graph, and re-finding it here costs one scan per update rather than the one
    // per TICK that checking for it in `rootCenter` would.
    this.root = undefined
  }

  chargeStrength = (d: GraphNode): number => {
    const base = CHARGE_BASE[d.type] ?? 0

    const degreeScaled = base * this.degreeScale(this.degreeForNode(d))
    return degreeScaled * this.siblingChargeScale(this.siblingCountForNode(d))
  }

  collideRadius = (d: GraphNode): number => {
    // A cloud location's box, and room round it. It has no siblings to scale with.
    if (d.cloudPlace) return CLOUD_PLACE_SIZE / 2 + 10
    const base = COLLIDE_BASE[d.type] ?? 8

    const siblingFactor = this.siblingCollisionScale(this.siblingCountForNode(d))
    const scaled = base * siblingFactor

    // Expandable nodes: minimal collision when collapsed, spread out when expanded
    if (d.expanded !== undefined) {
      return d.expanded ? scaled * EXPANDED_FORCE.collisionMultiplier : scaled
    }

    return scaled
  }

  /**
   * A link's rest length is the gap between the rings its two ends sit on, so the springs and the
   * layer force want the same picture instead of pulling against each other.
   *
   * ⚠ NOTHING IS ADDED FOR A BUSY END, AND THAT IS A MEASUREMENT, NOT AN OVERSIGHT. This used to
   * multiply the rest length by `1 + 0.15 * log(degree(source) + degree(target))`, which stretched
   * the spokes of a thirteen-node site by up to 1.46× and inflated the NETWORK ring beneath it
   * against the sites above. Crowding is a problem of ANGLE, and the wedges and `collide` are what
   * answer it; stretching the spoke only argues with the ring.
   *
   * Measured on the seventeen-node fleet in `layout.test.ts`: removing the growth took the graph
   * from 665×786 to 630×746 and the shared-name crossings from 120 to 113. The ring column says
   * why: with the growth in, the network ring inflates from 70 to 79 while the location ring
   * contracts from 140 to 135, so the two close on each other.
   *
   * ⚠ THE MOVEMENT IS THE NETWORK RING OUTWARD, NOT A SITE RING OUTWARD. `ringOf('location')` is
   * a MEDIAN over the fleet's two sites and with two values returns the LARGER, so 140 → 135 is
   * the four-node San Francisco hub, not the thirteen-node site this paragraph is about. That site
   * sits at 114 and moves INWARD to 111 when the growth is in; San Francisco moves inward too.
   * No site ring moves out. Look for the account→network rest length, not `layerForce`'s radial
   * term, if you are chasing where the extent went.
   *
   * ⚠ `node --experimental-strip-types src/graph/layout.test.ts` prints the SHIPPED half of each
   * pair and nothing else. The other half needs the growth put back in a scratch copy. The term is
   * RECONSTRUCTED from these figures and appears in no merged commit, so `git log -S` will not
   * find it and there is nothing to diff against — rebuild it from the line quoted above.
   *
   * ⚠⚠ NO TEST CATCHES THE RULE COMING BACK, AND THAT IS WHY THESE NUMBERS ARE HERE. Put the
   * growth back and the suite passes CLEAN: 786 is under the `height < 2400` sanity bound and 120
   * is under `fanCrossings < 130`. The `height < 760` gate that would have caught it went with
   * #310, when `centerGraph` learnt to shrink (see `viewport.ts`) and that assertion stopped being
   * a design limit. So a green suite is NOT evidence the growth is still out — re-run the fixture
   * and compare against the figures above.
   *
   * ⚠ These figures are the SECOND set this comment has carried. The first was written part way
   * through the change and shipped describing a graph the merged constants do not draw (app#319);
   * it said 667×745 → 606×698, 126 → 116, and *twice* the rest length, and not one of the five
   * reproduced. If you tune the constants above, re-run the fixture and correct these too.
   */
  linkDistance = (d: GraphLink): number => {
    // Extend links to expanded nodes
    const expandedExtension = d.expanded ? EXPANDED_FORCE.linkExtension : 0

    return this.layerGap(d) + expandedExtension
  }

  /**
   * d3's own default, `1 / min(degree(source), degree(target))`: a link into a busy node pulls
   * less, or a hub would reel its children in.
   */
  linkStrength = (d: GraphLink): number => {
    const degS = this.degreeForLinkEnd(d.source)
    const degT = this.degreeForLinkEnd(d.target)
    return 1 / Math.min(degS, degT)
  }

  /**
   * Holds each node near its layer's ring, around the account rather than around the SVG origin.
   *
   * ⚠ The origin is not the account. `state.centerLoadedNodes` shifts every restored position so
   * the BOUNDING BOX is centred, which is not where the account ends up, and `layout.ts` seeds a
   * fresh graph around whichever root already had coordinates. A force anchored at (0,0) would
   * quietly drag the whole hierarchy off its own centre on every reload, so this one reads the
   * root's live position each tick.
   */
  layerForce = (): d3.Force<GraphNode, GraphLink> => {
    let nodes: GraphNode[] = []

    const force = (alpha: number) => {
      const { x: cx, y: cy } = this.rootCenter(nodes)
      const radialK = LAYER_STRENGTH * alpha
      const angularK = ANGULAR_STRENGTH * alpha

      nodes.forEach(node => {
        if (node.x === undefined || node.y === undefined) return

        const dx = node.x - cx || 1e-6
        const dy = node.y - cy || 1e-6
        const distance = Math.sqrt(dx * dx + dy * dy)

        const radius = this.layerRadius(node)
        if (radius !== undefined) {
          // Positive when the node is inside its ring, so the pull is always toward the ring.
          const step = ((radius - distance) * radialK) / distance
          node.vx = (node.vx ?? 0) + dx * step
          node.vy = (node.vy ?? 0) + dy * step
        }

        // …and around to the bearing its parent gave it, which is what keeps a site inside its
        // own wedge instead of letting it slide round the ring until it wraps the whole circle.
        const anchor = this.angles.get(node.id)
        if (anchor === undefined) return

        const angle = Math.atan2(dy, dx)
        // Shortest way round, so a wedge that straddles due-west is not torn in half.
        let delta = anchor - angle
        while (delta > Math.PI) delta -= 2 * Math.PI
        while (delta < -Math.PI) delta += 2 * Math.PI

        // Tangential, so the wedge closes without changing which ring the node is on.
        const arc = delta * distance * angularK
        node.vx = (node.vx ?? 0) - Math.sin(angle) * arc
        node.vy = (node.vy ?? 0) + Math.cos(angle) * arc
      })
    }

    force.initialize = (simulationNodes: GraphNode[]) => {
      nodes = simulationNodes
    }

    return force as d3.Force<GraphNode, GraphLink>
  }

  private layerRadius = (node: GraphNode): number | undefined => LAYER_RADIUS[node.type]

  rootCenter = (nodes: GraphNode[]): { x: number; y: number } => {
    if (!this.root) {
      this.root = nodes.find(node => node.type === 'account')
    }

    if (this.root?.x !== undefined && this.root?.y !== undefined) {
      return { x: this.root.x, y: this.root.y }
    }

    return { x: 0, y: 0 }
  }

  private layerGap = (d: GraphLink): number => {
    const from = this.layerRadiusForEnd(d.source, d.sourceType)
    const to = this.layerRadiusForEnd(d.target, d.targetType)
    if (from === undefined || to === undefined) return DEFAULT_LINK_DISTANCE

    // `abs` so a link whose ends are read in the other order cannot ask for a negative length.
    return Math.max(Math.abs(to - from), 1)
  }

  /**
   * ⚠⚠ THE NODE'S OWN `type` FIRST, AND THE LINK'S `sourceType` / `targetType` ONLY AS A FALLBACK.
   * Those two strings are not the ring the ends sit on and were never meant to be: the
   * `network → location` link carries `targetType: 'node'`, and a `location → node` link carries
   * `gateway` or `proxy` while its node is streaming. Reading them made the network want to sit
   * 170 px out — the account-to-NODE gap — which parked the network shield in among the location
   * hubs it is supposed to sit inside of. d3 replaces a link's endpoint ids with the node objects
   * when `forceLink.links()` runs, so by the first tick the real type is right there.
   */
  private layerRadiusForEnd = (end: string | GraphNode, fallback: string): number | undefined => {
    if (typeof end !== 'string') return this.layerRadius(end)
    return LAYER_RADIUS[fallback as GraphNode['type']]
  }

  private degreeForLinkEnd = (end: string | GraphNode): number => {
    const id = typeof end === 'string' ? end : end.id
    return this.degreeForId(id)
  }

  private degreeForNode = (node: GraphNode): number => {
    return this.degreeForId(node.id)
  }

  private degreeForId = (id: string): number => {
    return Math.max(1, this.degrees.get(id) ?? 1)
  }

  private degreeScale = (degree: number): number => {
    return 1 / (1 + Math.log(degree))
  }

  private siblingCountForNode = (node: GraphNode): number => {
    return this.siblingCountForId(node.id)
  }

  private siblingCountForId = (id: string): number => {
    return Math.max(0, this.siblings.get(id) ?? 0)
  }

  private siblingCollisionScale = (siblingCount: number): number => {
    return this.siblingRamp(siblingCount, COLLIDE_SIBLING_MIN_FACTOR, COLLIDE_SIBLING_MAX_FACTOR)
  }

  private siblingChargeScale = (siblingCount: number): number => {
    return this.siblingRamp(siblingCount, CHARGE_SIBLING_MIN_FACTOR, CHARGE_SIBLING_MAX_FACTOR)
  }

  /** `minFactor` at 0-1 siblings, ramping to `maxFactor` at `SIBLING_MAX`. */
  private siblingRamp = (siblingCount: number, minFactor: number, maxFactor: number): number => {
    if (siblingCount <= 0) return minFactor
    const capped = Math.min(siblingCount, SIBLING_MAX)
    const ratio = (capped - 1) / Math.max(1, SIBLING_MAX - 1)
    return minFactor + (maxFactor - minFactor) * ratio
  }
}

/**
 * How hard the ring pulls. Deliberately gentle: it decides the DEPTH a node settles at and leaves
 * the angle to the links and the charge. Turn it up and the graph stops being a force layout and
 * becomes a set of concentric circles that ignore who is connected to whom.
 */
const LAYER_STRENGTH = 0.35

/**
 * How hard a child is pulled around to its parent's bearing.
 *
 * ⚠⚠ THIS IS WHAT KEEPS A SITE TOGETHER, AND IT IS NOT OBVIOUS THAT IT WOULD BE. The ring force
 * alone gives a clean hierarchy and a terrible picture: seventeen nodes will not fit around a
 * 250 px ring at their keep-out radius, so a site's nodes fan out until they wrap the whole
 * circle. Holding each site in one wedge makes its nodes NEIGHBOURS.
 *
 * ⚠ Gentle on purpose. It sets the bearing a node drifts toward and leaves the spacing within
 * the wedge to `collide`; wound up, it stacks every sibling on one radius and the fan collapses
 * into a line.
 */
const ANGULAR_STRENGTH = 0.3

/**
 * How much velocity a node keeps from one tick to the next, as d3's `velocityDecay` — which is
 * friction, so HIGHER means slower. It is the lever for how fast the picture MOVES, and it is a
 * different question from `alphaDecay`, which only says how long it keeps moving: cooling faster
 * makes a fast rearrangement end sooner, it does not make it followable.
 *
 * ⚠⚠ THESE ARE SET ON EVERY RESTART, BOTH OF THEM, BECAUSE `velocityDecay` IS SIMULATION STATE
 * AND NOT A RESTART ARGUMENT. Setting the hover value and leaving it would damp the next full
 * re-layout too, and the graph would have got permanently sluggish the first time anyone moved
 * the cursor over a node.
 */
export const LAYOUT_VELOCITY_DECAY = 0.4 // d3's own default: the initial layout is unchanged

/**
 * The settle after a hover or a selection, where a handful of leaves appear and their neighbours
 * shove over to make room.
 *
 * ⚠⚠ FRICTION IS WHAT STOPS THE CIRCLING, AND THE CIRCLING IS THE COMPLAINT. `layerForce` puts a
 * node back on its bearing with a purely TANGENTIAL term (`ANGULAR_STRENGTH`), which is an
 * angular spring with no damping of its own — the global `velocityDecay` is the only thing
 * resisting it. Underdamped, a displaced node therefore does not go to its place, it orbits
 * there, and the term scales with `distance`, so the outermost ring orbits fastest and most
 * visibly. Measured on the seventeen-node fleet as path walked per unit of net displacement,
 * where 1.00 is a node travelling straight to where it belongs:
 *
 *     velocityDecay   0.40    0.62    0.70    0.78    0.85
 *     path / net      1.65    1.24    1.19    1.08    1.04
 *     px per frame    20.3    12.8    10.1     7.4     5.1
 *
 * ⚠ `HOVER_ALPHA_DECAY` goes UP with the friction, not down, and an earlier version of this
 * comment had that backwards. The fear was that heavier damping covers less ground per tick and
 * so needs longer to arrive. It does cover less ground — but this restart is a gentle nudge at
 * `HOVER_ALPHA`, not a re-solve, and it has never restored the layout to equilibrium at ANY
 * setting: perturb the fleet and the worst node still sits ~76 px from where a full settle puts
 * it, at every combination measured, the shipped one included. So the extra ticks bought almost
 * nothing (a mean 26 px short instead of 32 px) and cost more than twice the duration. Cooling
 * promptly is the better trade, and it is what makes the movement stop rather than drift.
 */
export const HOVER_VELOCITY_DECAY = 0.78
export const HOVER_ALPHA = 0.1
export const HOVER_ALPHA_DECAY = 0.08

/**
 * How much of the centring offset `forceCenter` takes out per tick. d3's default is 1 — the whole
 * of it, immediately — and see the note at the `center` force for why that is wrong here.
 *
 * ⚠ It must not go too low either: this is the only thing that stops the cluster drifting off
 * the origin, and the viewport's fit is measured from where the nodes actually are. At 0.06 a
 * 40 px shift is 90% corrected inside 37 frames, well within a settle.
 */
const CENTER_STRENGTH = 0.06

/**
 * The simulation, wired to one `Force`. Lives here rather than in `core.ts` so the whole force
 * model can be run without a DOM — `layout.test.ts` builds a fleet, ticks this to rest and
 * measures the picture it produces.
 */
export function createSimulation(force: Force): d3.Simulation<GraphNode, GraphLink> {
  return d3
    .forceSimulation<GraphNode, GraphLink>()
    .force(
      'link',
      d3
        .forceLink<GraphNode, GraphLink>()
        .id((d: GraphNode) => d.id)
        .distance(force.linkDistance)
        .strength(force.linkStrength)
    )
    .force('charge', d3.forceManyBody<GraphNode>().strength(force.chargeStrength))
    // Holds each type on its own ring around the account, and each child near the bearing its
    // parent gave it. Replaces a `forceRadial` whose radius came from the link-distance table,
    // which is 0 for a service — so the one force meant to keep the layers apart
    // was pulling every leaf to the middle of the picture.
    .force('layer', force.layerForce())
    // ⚠ A rigid translation, not a shape: `forceCenter` moves every node by the same vector so
    // their mean sits on the origin, which the viewport maps to the middle of the screen. It is
    // needed because the layer force pushes outward from the account while the charge pushes the
    // account itself around, and the pair leaves the whole cluster drifting — the graph settled a
    // thousand units off-origin, half of it outside the window, and nothing recentres it after
    // the one `centerGraph` in the constructor.
    //
    // ⚠⚠ AND IT IS EASED, BECAUSE AT FULL STRENGTH IT IS THE FASTEST THING ON SCREEN. It writes
    // `x` and `y` DIRECTLY — it never touches velocity — so neither `velocityDecay` nor `alpha`
    // damps it, and at the default strength of 1 it corrects the whole offset in ONE tick. Every
    // leaf that appears or disappears moves the mean, so pointing at a node and pointing away
    // again each teleported the entire graph sideways to recentre it: measured at 43 px in a
    // single frame on a seventeen-node fleet, against 7.4 for the springs it was drowning out.
    // At `CENTER_STRENGTH` the same correction is spread over a couple of dozen frames, which
    // reads as the picture easing back rather than jumping.
    .force('center', d3.forceCenter<GraphNode>(0, 0).strength(CENTER_STRENGTH))
    .force(
      'collide',
      // ⚠ A cloud location collides: it is a 36 px box with its name, not a dot (`drawCloudPlace`).
      createFilteredCollide(force.collideRadius, d => d.type !== 'location' || !!d.cloudPlace)
    )
}

function createFilteredCollide(
  radius: (d: GraphNode) => number,
  filter: (d: GraphNode) => boolean
): d3.Force<GraphNode, GraphLink> {
  const base = d3.forceCollide<GraphNode>().radius(radius)
  let filtered: GraphNode[] = []

  const force: d3.Force<GraphNode, GraphLink> = (alpha: number) => {
    if (!filtered.length) return
    base(alpha)
  }

  force.initialize = (nodes: GraphNode[], random: () => number) => {
    filtered = nodes.filter(filter)
    base.initialize(filtered, random)
  }

  return force
}

/**
 * How many links touch each id, and how many children each parent has.
 *
 * ⚠ HERE RATHER THAN IN `core.ts` SO THE GATE CAN USE THE REAL ONES. They feed `setDegrees` and
 * `setSiblings`, which decide charge and collide — so a test that keeps its own
 * copies measures a force model that only happens to match the app. `core.ts` needs a DOM to
 * import; this file does not.
 */
export function buildDegreeMap(links: GraphLink[]): Map<string, number> {
  const degrees = new Map<string, number>()

  const bump = (id: string | undefined) => {
    if (!id) return
    const next = (degrees.get(id) ?? 0) + 1
    degrees.set(id, next)
  }

  links.forEach(link => {
    const sourceId = typeof link.source === 'string' ? link.source : link.source?.id
    const targetId = typeof link.target === 'string' ? link.target : link.target?.id
    bump(sourceId)
    bump(targetId)
  })

  return degrees
}

export function buildSiblingMap(links: GraphLink[]): Map<string, number> {
  const siblings = new Map<string, number>()
  const childrenByParent = new Map<string, string[]>()

  const addChild = (parentId: string | undefined, childId: string | undefined) => {
    if (!parentId || !childId) return
    const arr = childrenByParent.get(parentId)
    if (arr) {
      arr.push(childId)
    } else {
      childrenByParent.set(parentId, [childId])
    }
  }

  links.forEach(link => {
    const sourceId = typeof link.source === 'string' ? link.source : link.source?.id
    const targetId = typeof link.target === 'string' ? link.target : link.target?.id
    addChild(sourceId, targetId)
  })

  childrenByParent.forEach(children => {
    const siblingCount = Math.max(0, children.length - 1)
    children.forEach(childId => siblings.set(childId, siblingCount))
  })

  return siblings
}
